/**
 * Authentication Service
 * Manages Passcode Gate token lifecycle, persistence, and Cloudflare Pages Auth API integration with Vite preview fallback.
 */

import { AuthResponse, LoginCredentials } from '@/types/auth';

const TOKEN_KEY = 'bp_auth_token';
const SAVED_PASSCODE_KEY = 'bp_auth_saved_passcode';
const EXPECTED_PASSCODE = '9015';

/**
 * Get current stored auth token
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

/**
 * Set stored auth token
 */
export function setAuthToken(token: string, rememberMe = true): void {
  if (rememberMe) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
  }
}

/**
 * Clear stored auth token
 */
export function removeAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SAVED_PASSCODE_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

export const clearAuthToken = removeAuthToken;

/**
 * Check if the user is authenticated locally
 */
export function isAuthenticated(): boolean {
  return Boolean(getAuthToken());
}

/**
 * Login with passcode (default: 9015)
 * Connects to /api/auth/login with seamless fallback to client-side verification if server is offline.
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const password = credentials.password.trim();
  const rememberMe = credentials.rememberMe !== false;

  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data?.token) {
        setAuthToken(data.data.token, rememberMe);
        if (rememberMe) {
          localStorage.setItem(SAVED_PASSCODE_KEY, password);
        }
        return {
          success: true,
          token: data.data.token,
          expiresIn: data.data.expiresIn,
        };
      }
    } else if (response.status === 401) {
      const errData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errData.error || '通行密碼錯誤，請重新確認 (Invalid passcode)',
      };
    }
  } catch (err) {
    // Network or static Vite preview fallback
    console.warn('Backend /api/auth/login unreachable, fallback to client-side validation:', err);
  }

  // Client-side fallback check
  if (password === EXPECTED_PASSCODE) {
    const mockToken = `local_token_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setAuthToken(mockToken, rememberMe);
    if (rememberMe) {
      localStorage.setItem(SAVED_PASSCODE_KEY, password);
    }
    return {
      success: true,
      token: mockToken,
      expiresIn: 86400 * 30,
    };
  }

  return {
    success: false,
    error: '通行密碼錯誤，請重新確認 (Invalid passcode)',
  };
}

/**
 * Verify current authentication token with server
 */
export async function verifyAuth(): Promise<AuthResponse> {
  const token = getAuthToken();
  if (!token) {
    return { success: false, authenticated: false, error: '未登入或憑證已過期' };
  }

  try {
    const response = await fetch('/api/auth/verify', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        authenticated: data.data?.authenticated ?? true,
      };
    } else if (response.status === 401) {
      removeAuthToken();
      return {
        success: false,
        authenticated: false,
        error: '認證憑證無效或已過期',
      };
    }
  } catch (err) {
    // Network / static preview fallback: trust non-empty token
    console.warn('Backend /api/auth/verify unreachable, trusting local token:', err);
    return {
      success: true,
      authenticated: true,
    };
  }

  return {
    success: false,
    authenticated: false,
  };
}

/**
 * Logout and clear authentication session
 */
export async function logout(): Promise<void> {
  const token = getAuthToken();
  if (token) {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).catch(() => {});
    } catch {
      // Ignore network errors during logout
    }
  }
  removeAuthToken();
}
